import { GcRegistryBridgeDto, gcRegistryBridgePaths } from "@flexidao/dto";
import { ApiClient } from "../api-client";
import { gcRegistryBridgeDecoders } from "../decoders";
import { ApiResponse, ApiResultResponse, OverloadApiResponse } from "../types";
import { buildQueries, crash } from "../utils";

export type GcRegistryBridgeBaseArgs = {
    getAccessToken: () => Promise<string>;
    tenantId: string;
};

export type OverloadGcRegistryBridgeClient = {
    getTermsConditionsByTenantId: (
        args: GcRegistryBridgeBaseArgs,
    ) => OverloadApiResponse<GcRegistryBridgeDto.GetTermsConditionsByTenantIdResponse>;
    postAcceptTermsConditionsByTenantId: (
        args: GcRegistryBridgeBaseArgs &
            GcRegistryBridgeDto.PostAcceptTermsConditionsByTenantIdPayload,
    ) => OverloadApiResponse<GcRegistryBridgeDto.PostAcceptTermsConditionsByTenantIdResponse>;
} & ApiClient<gcRegistryBridgePaths>;

export type GcRegistryBridgeClient = {
    getTermsConditionsByTenantId: (
        args: GcRegistryBridgeBaseArgs,
    ) => ApiResponse<GcRegistryBridgeDto.GetTermsConditionsByTenantIdResponse>;
    postAcceptTermsConditionsByTenantId: (
        args: GcRegistryBridgeBaseArgs &
            GcRegistryBridgeDto.PostAcceptTermsConditionsByTenantIdPayload,
    ) => ApiResponse<GcRegistryBridgeDto.PostAcceptTermsConditionsByTenantIdResponse>;
} & ApiClient<gcRegistryBridgePaths>;

export type ResultGcRegistryBridgeClient = {
    getTermsConditionsByTenantId: (
        args: GcRegistryBridgeBaseArgs,
    ) => ApiResultResponse<GcRegistryBridgeDto.GetTermsConditionsByTenantIdResponse>;
    postAcceptTermsConditionsByTenantId: (
        args: GcRegistryBridgeBaseArgs &
            GcRegistryBridgeDto.PostAcceptTermsConditionsByTenantIdPayload,
    ) => ApiResultResponse<GcRegistryBridgeDto.PostAcceptTermsConditionsByTenantIdResponse>;
} & ApiClient<gcRegistryBridgePaths>;

export function getGcRegistryBridgeClient(config: { useRetries?: boolean }): GcRegistryBridgeClient;
export function getGcRegistryBridgeClient(config: {
    useResult: boolean;
}): ResultGcRegistryBridgeClient;
export function getGcRegistryBridgeClient(config: {
    useResult?: boolean;
    useRetries?: boolean;
}): Omit<
    OverloadGcRegistryBridgeClient,
    | "putAdminEventsTrigger"
    | "postAdminTriggerBackgroundWorker"
    | "getAdminInspectIndicators"
    | "getHistoricalMeterMeteredReadingsByTenantId"
    | "getMetersByTenantId"
    | "postLocalTenantsByTenantId"
    | "getEacsByTenantId"
    | "postEacsQueueByTenantId"
    | "postEacsRetryByTenantId"
    | "getDevicesByTenantId"
    | "postDevicesByTenantId"
> {
    let url: string = "";

    let useMockData = false;
    if (typeof window !== "undefined") {
        url = window.location.origin;
    } else if (typeof process !== "undefined") {
        if (process.env.MOCK_GC_REGISTRY_BRIDGE == "true") {
            useMockData = true;
        }
        if (!process.env.GC_REGISTRY_BRIDGE_URL) {
            return crash("Missing GC_REGISTRY_BRIDGE_URL environment variable.");
        } else {
            url = process.env.GC_REGISTRY_BRIDGE_URL;
        }
    } else {
        return crash("No window or process, where the hell are we??!");
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    buildQueries({ ...config, useMockData: useMockData }, url);

    const { get, post } = buildQueries({ ...config, useMockData: useMockData }, url);

    return {
        getTermsConditionsByTenantId: async ({
            getAccessToken,
            tenantId,
        }): OverloadApiResponse<GcRegistryBridgeDto.GetTermsConditionsByTenantIdResponse> => {
            const accessToken = await getAccessToken();

            return get({
                path: `/api/gc-registry-bridge/${tenantId}/terms-conditions`,
                decoder: gcRegistryBridgeDecoders.getTermsConditionsDecoder,
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
        },

        postAcceptTermsConditionsByTenantId: async ({
            getAccessToken,
            tenantId,
            ...body
        }): OverloadApiResponse<GcRegistryBridgeDto.PostAcceptTermsConditionsByTenantIdResponse> => {
            const accessToken = await getAccessToken();

            return post({
                path: `/api/gc-registry-bridge/${tenantId}/accept-terms-conditions`,
                data: body,
                decoder: gcRegistryBridgeDecoders.postAcceptTermsConditionsDecoder,
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
        },
    };
}
